import { useParams, useSearchParams } from "react-router-dom";
import {
  DASHBOARD_SURVEY_LIST_PAGE_SIZE,
  ENTITY_FILTER_LIST,
  EntityFilter,
  FILTER_STATUS_LIST,
  FilterStatus,
} from "./dashboard-survey-list.constants";
import { useDeleteSurveyMutation, useGetSurveysQuery } from "../../../redux/survey/survey.endpoints";
import { FetchSurveyData } from "../../../services/surveys/survey.types";
import { useEffect, useMemo } from "react";
import { useSurveyGroups } from "../hooks/use-survey-groups";
import type { SurveyGroup } from "../../../types/survey-group.types";
import type { Survey } from "../../../redux/survey/survey.types";
import { times } from "lodash";

export type SurveyListRow =
  | { kind: 'survey'; survey: Survey | undefined }
  | { kind: 'group'; group: SurveyGroup | undefined };

export interface UseDashboardSurveyListReturn {
  readonly activePage: number;
  readonly activeStatus?: FilterStatus;
  readonly activeEntity: EntityFilter;
  readonly filteredResults: boolean;
  readonly showAllUsers?: boolean;
  readonly onFilterSelected: (option: string) => void;
  readonly onEntitySelected: (entity: EntityFilter) => void;
  readonly data?: FetchSurveyData;
  readonly isFetching: boolean;
  readonly onPageChanged: (page: number) => void;
  readonly onSurveyDeletion: (extSurveyId: string) => void;
  readonly onTitleSearch: (title: string) => void;
  readonly onUserFilterSelected: (showAllUsers: boolean) => void;
  readonly refetch: () => void;
  readonly listRows: SurveyListRow[];
  readonly listTotalPages: number;
  readonly listTotalObjects: number;
  readonly isListEmpty: boolean;
}

export const isValidStatusParam = (status: string): status is FilterStatus => {
  return FILTER_STATUS_LIST.includes(status as FilterStatus);
};

export const isValidEntityParam = (entity: string): entity is EntityFilter => {
  return ENTITY_FILTER_LIST.includes(entity as EntityFilter);
};

export const useDashboardSurveyList = (): UseDashboardSurveyListReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status') ?? '';
  const titleParam = searchParams.get('title') ?? '';
  const allUsersParam = searchParams.get('allUsers') ?? '';
  const entityParam = searchParams.get('entity') ?? '';
  const activeStatus = isValidStatusParam(statusParam) ? statusParam : undefined;
  const activeEntity: EntityFilter = isValidEntityParam(entityParam) ? entityParam : 'all';
  const pageParam = searchParams.get('page') ?? '1';
  const activePage = parseInt(pageParam, 10);
  const showAllUsers = allUsersParam === 'true';
  const { clientId, tab } = useParams<{ clientId?: string; tab?: string }>();
  /** Combine flow only lists surveys; always fetch and ignore entity=groups for this route */
  const isCombineSurveyRoute = tab === 'combine';
  const { groups } = useSurveyGroups();

  const filteredGroups = useMemo(() => {
    const t = titleParam.trim().toLowerCase();
    if (!t) {
      return groups;
    }
    return groups.filter((g) => g.name.toLowerCase().includes(t));
  }, [groups, titleParam]);

  const skipSurveyApi = activeEntity === 'groups' && !isCombineSurveyRoute;

  const { data, isFetching, refetch } = useGetSurveysQuery(
    {
      page: activePage,
      survey_status: activeStatus ?? FILTER_STATUS_LIST.join(','),
      page_size: DASHBOARD_SURVEY_LIST_PAGE_SIZE,
      title: titleParam,
      user: !showAllUsers,
    },
    { skip: skipSurveyApi }
  );

  const [deleteSurvey, { isSuccess: deleteSuccess }] = useDeleteSurveyMutation();

  const filteredResults = Boolean(activeStatus || titleParam || activeEntity !== 'all');

  const groupTotalPages = Math.max(1, Math.ceil(filteredGroups.length / DASHBOARD_SURVEY_LIST_PAGE_SIZE));

  const pagedGroups = useMemo(() => {
    const start = (activePage - 1) * DASHBOARD_SURVEY_LIST_PAGE_SIZE;
    return filteredGroups.slice(start, start + DASHBOARD_SURVEY_LIST_PAGE_SIZE);
  }, [filteredGroups, activePage]);

  const listTotalPages = useMemo(() => {
    if (isCombineSurveyRoute) {
      return data?.totalPages ?? 1;
    }
    if (activeEntity === 'groups') {
      return groupTotalPages;
    }
    return data?.totalPages ?? 1;
  }, [isCombineSurveyRoute, activeEntity, groupTotalPages, data?.totalPages]);

  const listTotalObjects = useMemo(() => {
    if (isCombineSurveyRoute) {
      return data?.totalObjects ?? 0;
    }
    if (activeEntity === 'groups') {
      return filteredGroups.length;
    }
    if (activeEntity === 'surveys') {
      return data?.totalObjects ?? 0;
    }
    return (data?.totalObjects ?? 0) + filteredGroups.length;
  }, [isCombineSurveyRoute, activeEntity, filteredGroups.length, data?.totalObjects]);

  const listRows = useMemo((): SurveyListRow[] => {
    if (isCombineSurveyRoute) {
      const surveys = data?.surveys;
      const surveySkeleton = (): SurveyListRow[] =>
        times(DASHBOARD_SURVEY_LIST_PAGE_SIZE, () => ({ kind: 'survey', survey: undefined }));
      if (isFetching && (surveys?.length ?? 0) === 0) {
        return surveySkeleton();
      }
      return (surveys ?? []).map((s) => ({ kind: 'survey', survey: s }));
    }

    if (activeEntity === 'groups') {
      return pagedGroups.map((g) => ({ kind: 'group', group: g }));
    }

    const surveys = data?.surveys;
    const surveySkeleton = (): SurveyListRow[] =>
      times(DASHBOARD_SURVEY_LIST_PAGE_SIZE, () => ({ kind: 'survey', survey: undefined }));

    if (activeEntity === 'surveys') {
      if (isFetching && (surveys?.length ?? 0) === 0) {
        return surveySkeleton();
      }
      return (surveys ?? []).map((s) => ({ kind: 'survey', survey: s }));
    }

    const groupRows: SurveyListRow[] = filteredGroups.map((g) => ({ kind: 'group', group: g }));
    if (isFetching && (surveys?.length ?? 0) === 0) {
      return [...groupRows, ...surveySkeleton()];
    }
    const surveyRows: SurveyListRow[] = (surveys ?? []).map((s) => ({ kind: 'survey', survey: s }));
    return [...groupRows, ...surveyRows];
  }, [isCombineSurveyRoute, activeEntity, pagedGroups, filteredGroups, data?.surveys, isFetching]);

  const isListEmpty = useMemo(() => {
    if (isCombineSurveyRoute) {
      if (isFetching) {
        return false;
      }
      return (data?.totalObjects ?? 0) === 0;
    }
    if (!skipSurveyApi && isFetching) {
      return false;
    }
    if (activeEntity === 'groups') {
      return filteredGroups.length === 0;
    }
    if (activeEntity === 'surveys') {
      return (data?.totalObjects ?? 0) === 0;
    }
    return filteredGroups.length === 0 && (data?.totalObjects ?? 0) === 0;
  }, [isCombineSurveyRoute, activeEntity, filteredGroups.length, data?.totalObjects, isFetching, skipSurveyApi]);

  useEffect(() => {
    if (clientId) {
      const activeClientId = localStorage.getItem('ActiveClientId');
      if (activeClientId !== clientId) {
        refetch();
        localStorage.setItem('ActiveClientId', clientId);
      }
    }
  }, [clientId, refetch]);

  useEffect(() => {
    if (!deleteSuccess) {
      return;
    }
    if (skipSurveyApi) {
      return;
    }
    const currentPageLength = data?.surveys.length ?? 0;
    const shouldGoBack = currentPageLength - 1 <= 0 && activePage > 1;
    if (shouldGoBack) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', (activePage - 1).toString());
      setSearchParams(newSearchParams);
    } else {
      refetch();
    }
  }, [deleteSuccess]);

  const onFilterSelected = (option: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (isValidStatusParam(option)) {
      if (option !== statusParam) {
        newSearchParams.set('status', option);
        newSearchParams.delete('page');
        setSearchParams(newSearchParams);
      }
    } else {
      if (statusParam) {
        newSearchParams.delete('status');
        newSearchParams.delete('page');
        setSearchParams(newSearchParams);
      }
    }
  };

  const onUserFilterSelected = (showAllUsers: boolean) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (showAllUsers) {
      newSearchParams.set('allUsers', 'true');
    } else {
      newSearchParams.delete('allUsers');
    }
    newSearchParams.delete('page');
    setSearchParams(newSearchParams);
  };

  const onPageChanged = (page: number) => {
    if (page !== activePage) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', page.toString());
      setSearchParams(newSearchParams);
    }
  };

  const onTitleSearch = (title: string) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (title) {
      newSearchParams.set('title', title);
    } else {
      newSearchParams.delete('title');
    }
    newSearchParams.delete('page');
    setSearchParams(newSearchParams);
  };

  const onEntitySelected = (entity: EntityFilter) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    if (entity !== 'all') {
      newSearchParams.set('entity', entity);
    } else {
      newSearchParams.delete('entity');
    }
    newSearchParams.delete('page');
    setSearchParams(newSearchParams);
  };

  const onSurveyDeletion = (extSurveyId: string) => {
    void deleteSurvey({ ext_survey_id: extSurveyId });
  };

  return {
    activePage,
    activeStatus,
    activeEntity,
    filteredResults,
    showAllUsers,
    onFilterSelected,
    onEntitySelected,
    data,
    isFetching,
    onPageChanged,
    onSurveyDeletion,
    onTitleSearch,
    onUserFilterSelected,
    refetch,
    listRows,
    listTotalPages,
    listTotalObjects,
    isListEmpty,
  };
};
